import { CopyChip } from '@src/components/CopyChip';
import { JsonView } from '@src/components/JsonView';
import { PageHeader } from '@src/components/shared/PageHeader';
import { Badge } from '@src/components/ui/badge';
import { Button } from '@src/components/ui/button';
import { Card, CardContent } from '@src/components/ui/card';
import { errorMessage } from '@src/lib/errors';
import { cn } from '@src/lib/utils';
import { resources } from '@src/resources';
import { fetchEnrichmentRecord, outcomeLabel, type PromptPart, scopeLabel } from '@src/services/opsService';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import Markdown from 'react-markdown';
import { Link, useSearchParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

/** True when a unix-seconds record timestamp and an ISO run `createdAt` are close enough to be
 * "the same run" (within 5s) -- mirrors really-data-ui's RecordMetaPanel `sameInstant` check. */
function sameInstant(recordTimestamp: number | undefined, createdAtIso: string | undefined): boolean {
  if (recordTimestamp === undefined || createdAtIso === undefined) return false;
  const createdAtSeconds = new Date(createdAtIso).getTime() / 1000;
  return Math.abs(recordTimestamp - createdAtSeconds) < 5;
}

function formatRelativeTime(unixSeconds: number): string {
  const diffMs = Date.now() - unixSeconds * 1000;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function fullTimestamp(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toISOString();
}

/** Absolute UTC instant for display -- the record panel shows this directly rather than only
 * relative time, so a stale record's actual age is legible without hovering. */
function formatAbsoluteUtc(unixSeconds: number): string {
  return `${new Date(unixSeconds * 1000).toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}

function truncate(value: string, length = 16): string {
  return value.length > length ? `${value.slice(0, length)}…` : value;
}

/** Contracted height for a collapsed markdown block -- tall enough to orient on, short enough
 * that a long system prompt doesn't dominate the page by default. */
const MARKDOWN_COLLAPSED_HEIGHT = 'max-h-40';

/** Renders markdown text -- prompt content is frequently markdown-formatted (headings, lists,
 * code blocks quoting scraped listing text). `react-markdown` never interprets raw HTML by
 * default (no rehype-raw plugin here), so this stays safe against untrusted content, e.g. text
 * scraped from a third-party listing that ends up quoted in a prompt part.
 *
 * Collapsed (contracted) by default -- a full prompt can run to hundreds of lines, which would
 * otherwise dominate the page. Expanding is a plain toggle, not tied to the outer "Prompt sent"
 * disclosure, so a multi-part prompt's parts expand independently. */
function MarkdownBlock({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-md border border-border bg-background">
      <div className={cn('overflow-auto p-3', !expanded && `${MARKDOWN_COLLAPSED_HEIGHT} overflow-y-hidden`)}>
        <div className="prose prose-invert prose-sm max-w-none">
          <Markdown remarkPlugins={[remarkGfm]}>{text}</Markdown>
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="h-auto w-full justify-start rounded-none border-t border-border px-3 py-1.5 text-left text-xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground"
      >
        {expanded ? '▾ Show less' : '▸ Show more'}
      </Button>
    </div>
  );
}

function PromptPartView({ part }: { part: PromptPart }) {
  if (part.type === 'text' && part.text !== undefined) {
    return <MarkdownBlock text={part.text} />;
  }
  return (
    <div className="rounded-md border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
      {part.type}
      {part.mediaType ? ` · ${part.mediaType}` : ''}
      {part.ref ? ` · ${part.ref}` : ''}
    </div>
  );
}

function RecordRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-24 shrink-0 text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="flex min-w-0 items-center gap-2 text-sm">{children}</dd>
    </div>
  );
}

const NUM = new Intl.NumberFormat('en-US');

function formatUsd(usd: number): string {
  return `$${usd.toFixed(4)}`;
}

function formatLatency(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

export function RunDetail() {
  const [searchParams] = useSearchParams();
  const runId = searchParams.get('runId') ?? undefined;
  const propertyId = searchParams.get('propertyId') ?? '';
  const scope = searchParams.get('scope') ?? '';
  const createdAt = searchParams.get('createdAt') ?? undefined;
  const [showInput, setShowInput] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showProviderMetadata, setShowProviderMetadata] = useState(false);

  const { data: record, isLoading, isError, error } = useQuery({
    queryKey: ['enrichment-record', scope, propertyId],
    queryFn: () => fetchEnrichmentRecord(scope, propertyId),
    enabled: propertyId !== '' && scope !== '',
  });

  const metadata = record?.metadata;
  const isStale = Boolean(record) && !sameInstant(record?.timestamp, createdAt);
  const backToRunsHref = `/runs?scope=${encodeURIComponent(scope)}`;

  return (
    <div>
      <div className="mb-2">
        <Link to={backToRunsHref} className="text-sm text-primary hover:underline">
          {resources.runDetail.backToRuns}
        </Link>
      </div>
      <PageHeader title={resources.runDetail.title} subtitle={`${scopeLabel(scope)} · ${propertyId}`} />

      {isLoading && <p className="text-sm text-muted-foreground">{resources.runDetail.loading}</p>}
      {isError && (
        <div className="text-sm text-status-critical">
          <p>{resources.runDetail.loadError}</p>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{errorMessage(error)}</p>
        </div>
      )}

      {!isLoading && !isError && record === null && (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">{resources.runDetail.noRecord}</CardContent>
        </Card>
      )}

      {record && (
        <div className="space-y-4">
          {isStale && (
            <div className="rounded-lg border border-status-warning/30 bg-status-warning/10 px-4 py-2.5 text-sm text-status-warning">
              {resources.runDetail.staleWarning}
            </div>
          )}

          <Card>
            <CardContent className="space-y-2 p-4">
              <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                {resources.runDetail.recordPanelTitle}
              </span>
              <dl className="space-y-1.5">
                {record.timestamp !== undefined && (
                  <RecordRow label={resources.runDetail.lastRun}>
                    <time dateTime={fullTimestamp(record.timestamp)} title={fullTimestamp(record.timestamp)}>
                      {formatRelativeTime(record.timestamp)} · {formatAbsoluteUtc(record.timestamp)}
                    </time>
                  </RecordRow>
                )}
                {record.result !== undefined && (
                  <RecordRow label={resources.runDetail.result}>
                    <Badge variant="secondary">{outcomeLabel(record.result)}</Badge>
                  </RecordRow>
                )}
                {record.publishedAt !== undefined && !sameInstant(record.publishedAt, createdAt) && (
                  <RecordRow label={resources.runDetail.published}>
                    <time dateTime={fullTimestamp(record.publishedAt)} title={fullTimestamp(record.publishedAt)}>
                      {formatRelativeTime(record.publishedAt)} · {formatAbsoluteUtc(record.publishedAt)}
                    </time>
                  </RecordRow>
                )}
                {record.inputHash !== undefined && (
                  <RecordRow label={resources.runDetail.inputHash}>
                    <span className="truncate font-mono text-muted-foreground">{truncate(record.inputHash)}</span>
                    <CopyChip label="copy" value={record.inputHash} />
                  </RecordRow>
                )}
                {runId && (
                  <RecordRow label={resources.runDetail.runId}>
                    <span className="truncate font-mono text-muted-foreground">{truncate(runId)}</span>
                    <CopyChip label="copy" value={runId} />
                  </RecordRow>
                )}
                <RecordRow label={resources.runDetail.scope}>
                  <span className="font-mono text-muted-foreground">{scope}</span>
                </RecordRow>
              </dl>

              {metadata?.input !== undefined && (
                <div>
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setShowInput((v) => !v)}
                    aria-expanded={showInput}
                    className="h-auto p-0 text-sm font-normal text-muted-foreground hover:text-foreground hover:no-underline"
                  >
                    {showInput ? '▾' : '▸'} {resources.runDetail.resolvedInputToggle}
                  </Button>
                  {showInput && <JsonView value={metadata.input} label="Resolved input" defaultExpandDepth={1} />}
                </div>
              )}
            </CardContent>
          </Card>

          {metadata?.model && (
            <Card>
              <CardContent className="space-y-2 p-4">
                <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                  {resources.runDetail.aiMetricsPanelTitle}
                </span>
                <dl className="space-y-1.5">
                  <RecordRow label={resources.runDetail.model}>
                    <span className="font-mono">{metadata.model.used}</span>
                    {metadata.model.requested !== metadata.model.used && (
                      <span className="text-muted-foreground">· requested {metadata.model.requested}</span>
                    )}
                  </RecordRow>
                  {metadata.usage && (
                    <RecordRow label={resources.runDetail.tokens}>
                      <span className="font-mono">
                        {NUM.format(metadata.usage.inputTokens)} in · {NUM.format(metadata.usage.outputTokens)} out
                      </span>
                    </RecordRow>
                  )}
                  {metadata.cost && (
                    <RecordRow label={resources.runDetail.cost}>
                      <span className="font-mono">
                        {formatUsd(metadata.cost.usd)}
                        {metadata.cost.upstreamInferenceCostUsd !== undefined && (
                          <span className="text-muted-foreground">
                            {' '}
                            (model: {formatUsd(metadata.cost.upstreamInferenceCostUsd)} · other:{' '}
                            {formatUsd(metadata.cost.usd - metadata.cost.upstreamInferenceCostUsd)})
                          </span>
                        )}
                      </span>
                    </RecordRow>
                  )}
                  {metadata.latencyMs !== undefined && (
                    <RecordRow label={resources.runDetail.latency}>
                      <span className="font-mono">{formatLatency(metadata.latencyMs)}</span>
                    </RecordRow>
                  )}
                  {metadata.attempts !== undefined && (
                    <RecordRow label={resources.runDetail.attempts}>
                      <span className="font-mono">{metadata.attempts}</span>
                    </RecordRow>
                  )}
                  {metadata.traceId && (
                    <RecordRow label={resources.runDetail.trace}>
                      <span className="truncate font-mono text-muted-foreground">{truncate(metadata.traceId)}</span>
                      <CopyChip label="copy" value={metadata.traceId} />
                    </RecordRow>
                  )}
                  {metadata.providerGenerationId && (
                    <RecordRow label={resources.runDetail.generationId}>
                      <span className="truncate font-mono text-muted-foreground">
                        {truncate(metadata.providerGenerationId)}
                      </span>
                      <CopyChip label="copy" value={metadata.providerGenerationId} />
                    </RecordRow>
                  )}
                </dl>

                {metadata.providerMetadata !== undefined && (
                  <div>
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setShowProviderMetadata((v) => !v)}
                      aria-expanded={showProviderMetadata}
                      className="h-auto p-0 text-sm font-normal text-muted-foreground hover:text-foreground hover:no-underline"
                    >
                      {showProviderMetadata ? '▾' : '▸'} {resources.runDetail.providerMetadataToggle}
                    </Button>
                    {showProviderMetadata && (
                      <JsonView value={metadata.providerMetadata} label="Provider metadata" defaultExpandDepth={1} />
                    )}
                  </div>
                )}

                {metadata.promptOmitted ? (
                  <p className="text-sm text-muted-foreground">{resources.runDetail.promptOmitted}</p>
                ) : (
                  metadata.prompt && (
                    <div>
                      {metadata.prompt.system && (
                        <p className="truncate text-xs text-muted-foreground" title={metadata.prompt.system}>
                          System: {metadata.prompt.system}
                        </p>
                      )}
                      <Button
                        type="button"
                        variant="link"
                        onClick={() => setShowPrompt((v) => !v)}
                        aria-expanded={showPrompt}
                        className="h-auto p-0 text-sm font-normal text-muted-foreground hover:text-foreground hover:no-underline"
                      >
                        {showPrompt ? '▾' : '▸'} {resources.runDetail.promptToggle} (
                        {metadata.prompt.content.length} part{metadata.prompt.content.length === 1 ? '' : 's'})
                      </Button>
                      {showPrompt && (
                        <div className="mt-2 space-y-2">
                          {metadata.prompt.system && (
                            <div>
                              <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                                System
                              </span>
                              <MarkdownBlock text={metadata.prompt.system} />
                            </div>
                          )}
                          {metadata.prompt.content.map((part, i) => (
                            <PromptPartView key={i} part={part} />
                          ))}
                        </div>
                      )}
                    </div>
                  )
                )}
              </CardContent>
            </Card>
          )}

          {record.output !== undefined && record.output !== null && (
            <Card>
              <CardContent className="space-y-2 p-4">
                <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                  {resources.runDetail.outputPanelTitle}
                </span>
                <JsonView value={record.output} label={`${scopeLabel(scope)} output`} />
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
